import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useWindowSize } from '@react-hook/window-size';
import { useSelector, useDispatch } from 'react-redux';
import {
  useFirebase,
  useFirestoreConnect,
  isLoaded,
} from 'react-redux-firebase';
import moment from 'moment';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';

import { useTheme } from '../../ThemeContext';

import Modal from '../Modal';

import 'react-toastify/dist/ReactToastify.css';

import {
  Wrapper,
  Box,
  ImageHolder,
  Image,
  Video,
  Placeholder,
  ContentHolder,
  Header,
  Title,
  Content,
  Details,
  Subtitle,
  Detail,
  DetailLabel,
  Arrow,
  ArrowIcon,
  Footer,
  OptionButton,
  OptionButtonIcon,
  Grow,
} from './PhotoDocumentDetails.style';

import roles from '../../roles';
import { Breakpoints } from '../../assets/Variables.style';

const PhotoDocumentDetailsInner = ({
  data,
  handleClose = () => {},
  handleActionPhotoDocument = () => {},
  handleChangePhotoDocument = () => {},
  availableChangeLeftPhotoDocument = false,
  availableChangeRightPhotoDocument = false,
  availableChangeFilter = false,
  availableRelabel = false,
  availableReject = false,
  availableDelete = false,
  availableDownload = false,
}) => {
  const [width] = useWindowSize();
  const theme = useTheme();
  const intl = useIntl();
  const dispatch = useDispatch();
  const firebase = useFirebase();
  const { auth, profile } = useSelector((state) => state.firebase);
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const profileCompany = useSelector(
    (state) => state.firestore.data['profileCompany']
  );
  const profileCompanyRole =
    profileCompany &&
    profileCompany.admins &&
    profileCompany.admins.length > 0 &&
    profileCompany.admins.find((admin) => admin.id === auth.uid)
      ? 'isAdmin'
      : profileCompany &&
        profileCompany.users &&
        profileCompany.users.length > 0 &&
        profileCompany.users.find((user) => user.id === auth.uid)
      ? 'isUser'
      : undefined;
  const authRole = roles.find(
    (role) => profileCompanyRole && role.id === profileCompanyRole
  );
  const [storageVideo, setStorageVideo] = useState(undefined);
  const [storageImage, setStorageImage] = useState(undefined);
  const [storageImageError, setStorageImageError] = useState(false);
  const { isLoading: isLoadingRelabel } = useSelector(
    (state) => state.viewerPhotoDocumentRelabelStore
  );
  const { isLoading: isLoadingReject } = useSelector(
    (state) => state.viewerPhotoDocumentRejectStore
  );
  const { isLoading: isLoadingDelete } = useSelector(
    (state) => state.viewerPhotoDocumentDeleteStore
  );

  const dataSourcePhotoCategories = useSelector(
    (state) =>
      data &&
      data.data_source_id &&
      state.firestore.ordered[
        `data_sources/${data.data_source_id}/photo_categories`
      ]
  );
  const dataAutomaticAnnotationCategories = [
    ...((data &&
      data.automatic_annotation &&
      data.automatic_annotation.photo &&
      data.automatic_annotation.photo.tags &&
      data.automatic_annotation.photo.tags.map((mergedTag) => {
        const tag = mergedTag.split('.');
        const category =
          dataSourcePhotoCategories &&
          dataSourcePhotoCategories.find((category) => category.id === tag[0]);
        const categoryName = category && category.name;
        const tagName = category && category.tags && category.tags[tag[1]];

        return {
          name: categoryName || tag[0],
          value:
            tagName ||
            tag[1] ||
            intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
        };
      })) ||
      []),
  ];

  const dataVinneAnnotationCategories = [
    ...((data &&
      data.vinne_annotation &&
      data.vinne_annotation.photo &&
      data.vinne_annotation.photo.tags &&
      data.vinne_annotation.photo.tags.map((mergedTag) => {
        const tag = mergedTag.split('.');
        const category =
          dataSourcePhotoCategories &&
          dataSourcePhotoCategories.find((category) => category.id === tag[0]);
        const categoryName = category && category.name;
        const tagName = category && category.tags && category.tags[tag[1]];

        return {
          name: categoryName || tag[0],
          value:
            tagName ||
            tag[1] ||
            intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
        };
      })) ||
      []),
  ];

  const dataVinneVerificationCategories = [
    ...((data &&
      data.vinne_verification &&
      data.vinne_verification.photo &&
      data.vinne_verification.photo.tags &&
      data.vinne_verification.photo.tags.map((mergedTag) => {
        const tag = mergedTag.split('.');
        const category =
          dataSourcePhotoCategories &&
          dataSourcePhotoCategories.find((category) => category.id === tag[0]);
        const categoryName = category && category.name;
        const tagName = category && category.tags && category.tags[tag[1]];

        return {
          name: categoryName || tag[0],
          value:
            tagName ||
            tag[1] ||
            intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
        };
      })) ||
      []),
  ];

  const categoryCompare = (vinne, auto, data) => {
    let output = false;
    for (let i = 0; i < dataVinneAnnotationCategories.length; i++) {
      if (
        dataAutomaticAnnotationCategories[0].name ===
        dataVinneAnnotationCategories[i].name
      ) {
        output = true;
      }
    }
    return output;
  };

  const dataNNRecognition =
    data &&
    data.vinne_annotation &&
    data.vinne_annotation.photo &&
    data.vinne_annotation.photo.tags &&
    data.vinne_annotation.photo.tags.length > 0 &&
    data.automatic_annotation &&
    data.automatic_annotation.photo &&
    data.automatic_annotation.photo.tags &&
    data.automatic_annotation.photo.tags.length > 0
      ? categoryCompare(
          data.vinne_annotation.photo,
          data.automatic_annotation.photo,
          data
        )
        ? intl.formatMessage({
            id: 'PHOTO_DOCUMENT_DETAILS.NN_RECOGNITION.CORRECT',
          })
        : intl.formatMessage({
            id: 'PHOTO_DOCUMENT_DETAILS.NN_RECOGNITION.INCORRECT',
          })
      : '';

  const photoDocumentDetails = [
    {
      name: intl.formatMessage({
        id: `PHOTO_DOCUMENT_DETAILS.${
          data && data.video_id ? 'VIDEO_ID' : 'PHOTO_ID'
        }`,
      }),
      value:
        data && (data.video_id || data.photo_id)
          ? data.video_id
            ? data.video_id
            : data.photo_id
          : intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
    },
    {
      name: intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.ORIGIN_ID' }),
      value:
        (data && data.origin_id) ||
        intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
    },
    {
      name: intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.DATE_CREATED' }),
      value:
        data && data.creation_date
          ? moment.unix(data.creation_date).format('YYYY-MM-DD hh:mm:ss')
          : intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
    },
    {
      name: intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.DATE_RECEIVED' }),
      value:
        data && data.received_date
          ? moment.unix(data.received_date).format('YYYY-MM-DD hh:mm:ss')
          : intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
    },
    {
      name: intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.DATE_TAGGED' }),
      value:
        data && data.vinne_annotation && data.vinne_annotation.end_date
          ? moment.unix(data.vinne_annotation.end_date).format('YYYY-MM-DD')
          : intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
    },
    {
      name: intl.formatMessage({
        id: 'PHOTO_DOCUMENT_DETAILS.REJECTED',
      }),
      value:
        (data &&
          data.rejected !== undefined &&
          intl.formatMessage({
            id: `PHOTO_DOCUMENT_DETAILS.${String(data.rejected).toUpperCase()}`,
          })) ||
        intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
    },
    {
      name: intl.formatMessage({
        id: 'PHOTO_DOCUMENT_DETAILS.RESERVED',
      }),
      value:
        (data &&
          data.reserved !== undefined &&
          intl.formatMessage({
            id: `PHOTO_DOCUMENT_DETAILS.${String(data.reserved).toUpperCase()}`,
          })) ||
        intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
    },
    {
      name: intl.formatMessage({
        id: 'PHOTO_DOCUMENT_DETAILS.NN_VERSION',
      }),
      value:
        data &&
        data.automatic_annotation &&
        data.automatic_annotation.photo &&
        data.automatic_annotation.photo.neural_network_version
          ? data.automatic_annotation.photo.neural_network_version
          : intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
    },
    {
      name: intl.formatMessage({
        id: 'PHOTO_DOCUMENT_DETAILS.NN_RECOGNITION',
      }),
      value: dataNNRecognition
        ? dataNNRecognition
        : intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.NO_DATA' }),
    },
  ];

  const handleChangeFilter = (newFilters) => {
    dispatch({ type: 'FILTER_SET', payload: { ...newFilters } });
  };

  const handleDownload = async () => {
    if (storageVideo || storageImage) {
      const progressToastID = toast.info(
        intl.formatMessage({
          id: storageVideo
            ? `PENDING.COMMAND_DOWNLOAD_VIDEO`
            : storageImage && `PENDING.COMMAND_DOWNLOAD_IMAGE`,
        }),
        {
          autoClose: false,
          pauseOnFocusLoss: false,
        }
      );
      try {
        if (storageVideo) {
          const blob = await fetch(storageVideo).then((res) => res.blob());
          saveAs(blob, `${data.photo_id}.mp4`);
        } else if (storageImage) {
          const blob = await fetch(storageImage).then((res) => res.blob());
          saveAs(blob, `${data.photo_id}.jpg`);
        }
        setTimeout(() => {
          toast.dismiss(progressToastID);
        }, 200);
      } catch {
        setTimeout(() => {
          toast.update(progressToastID, {
            render: intl.formatMessage({
              id: storageVideo
                ? `ERROR.COMMAND_DOWNLOAD_VIDEO`
                : storageImage && `ERROR.COMMAND_DOWNLOAD_IMAGE`,
            }),
            type: toast.TYPE.ERROR,
            pauseOnFocusLoss: false,
            autoClose: 5000,
          });
        }, 200);
      }
    }
  };

  const firestoreConnect = [];
  if (isProfileCompany)
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      storeAs: 'profileCompany',
    });
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    let didCancel = false;
    if (data && (data.video_id || data.photo_id))
      (async () => {
        try {
          if (data.video_id) {
            const storageVideo = await firebase
              .storage()
              .ref()
              .child(`waste_photos/${data.video_id}.mp4`)
              .getDownloadURL();
            if (!didCancel) setStorageVideo(storageVideo);
          } else if (data.photo_id) {
            const storageImage = await firebase
              .storage()
              .ref()
              .child(`waste_photos/${data.photo_id}.jpg`)
              .getDownloadURL();
            if (!didCancel) setStorageImage(storageImage);
          }
        } catch {
          if (!didCancel) setStorageImageError(true);
        }
      })();

    return () => {
      didCancel = true;
      setStorageVideo(undefined);
      setStorageImage(undefined);
      setStorageImageError(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data && (data.video_id || data.photo_id)]);

  useEffect(() => {
    if (!data) handleClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const renderPhotoDocumentDetail = (detail) => {
    return (
      <Detail theme={theme.photoDocumentDetails.detail}>
        <DetailLabel theme={theme.photoDocumentDetails.detailLabel}>
          {detail.name}:
        </DetailLabel>{' '}
        {detail.value}
      </Detail>
    );
  };

  return (
    <Wrapper theme={theme.photoDocumentDetails.wrapper}>
      <Box>
        <ImageHolder theme={theme.photoDocumentDetails.imageHolder}>
          {availableChangeLeftPhotoDocument ? (
            <Arrow
              theme={theme.photoDocumentDetails.arrow}
              left
              onClick={() => {
                handleChangePhotoDocument('prev');
              }}
            >
              <ArrowIcon className='material-icons'>
                keyboard_arrow_left
              </ArrowIcon>
            </Arrow>
          ) : (
            ''
          )}
          {data && (data.video_id || data.photo_id) ? (
            storageVideo ? (
              <>
                <Video
                  src={storageVideo}
                  alt={data.video_id}
                  loading='lazy'
                  playsInline
                  autoPlay
                  muted
                  loop
                >
                  <source src={storageVideo} type='video/mp4'></source>
                </Video>
              </>
            ) : storageImage ? (
              <Image src={storageImage} alt={data.photo_id} loading='lazy' />
            ) : storageImageError ? (
              <Placeholder className='material-icons'>
                image_not_supported
              </Placeholder>
            ) : (
              ''
            )
          ) : (
            <Placeholder className='material-icons'>
              image_not_supported
            </Placeholder>
          )}
          {availableChangeRightPhotoDocument ? (
            <Arrow
              theme={theme.photoDocumentDetails.arrow}
              right
              onClick={() => {
                handleChangePhotoDocument('next');
              }}
            >
              <ArrowIcon className='material-icons'>
                keyboard_arrow_right
              </ArrowIcon>
            </Arrow>
          ) : (
            ''
          )}
        </ImageHolder>
        <ContentHolder theme={theme.photoDocumentDetails.contentHolder}>
          <Header theme={theme.photoDocumentDetails.header}>
            <Title theme={theme.photoDocumentDetails.title}>
              {intl.formatMessage({
                id: 'PHOTO_DOCUMENT_DETAILS.DETAILS',
              })}
            </Title>
          </Header>
          <Content>
            <Subtitle theme={theme.photoDocumentDetails.subtitle}>
              {intl.formatMessage({
                id: 'PHOTO_DOCUMENT_DETAILS.COMMON',
              })}
            </Subtitle>
            <Details>
              {photoDocumentDetails.map((detail, key) => (
                <Fragment key={key}>
                  {renderPhotoDocumentDetail(detail)}
                </Fragment>
              ))}
            </Details>

            {dataAutomaticAnnotationCategories &&
            dataAutomaticAnnotationCategories.length > 0 ? (
              <>
                <Subtitle theme={theme.photoDocumentDetails.subtitle}>
                  {intl.formatMessage({
                    id: 'PHOTO_DOCUMENT_DETAILS.AUTOMATIC_ANNOTATION',
                  })}
                </Subtitle>
                <Details>
                  {dataAutomaticAnnotationCategories.map((detail, key) => (
                    <Fragment key={key}>
                      {renderPhotoDocumentDetail(detail)}
                    </Fragment>
                  ))}
                </Details>
              </>
            ) : (
              ''
            )}

            {dataVinneAnnotationCategories &&
            dataVinneAnnotationCategories.length > 0 ? (
              <>
                <Subtitle theme={theme.photoDocumentDetails.subtitle}>
                  {intl.formatMessage({
                    id: 'PHOTO_DOCUMENT_DETAILS.VINNE_ANNOTATION',
                  })}
                </Subtitle>
                <Details>
                  {dataVinneAnnotationCategories.map((detail, key) => (
                    <Fragment key={key}>
                      {renderPhotoDocumentDetail(detail)}
                    </Fragment>
                  ))}
                </Details>
              </>
            ) : (
              ''
            )}

            {dataVinneVerificationCategories &&
            dataVinneVerificationCategories.length > 0 ? (
              <>
                <Subtitle theme={theme.photoDocumentDetails.subtitle}>
                  {intl.formatMessage({
                    id: 'PHOTO_DOCUMENT_DETAILS.VINNE_VERIFICATION',
                  })}
                </Subtitle>
                <Details>
                  {dataVinneVerificationCategories.map((detail, key) => (
                    <Fragment key={key}>
                      {renderPhotoDocumentDetail(detail)}
                    </Fragment>
                  ))}
                </Details>
              </>
            ) : (
              ''
            )}
          </Content>
        </ContentHolder>
      </Box>
      <Footer theme={theme.photoDocumentDetails.footer}>
        {availableChangeFilter ? (
          <OptionButton
            theme={theme.button.normal}
            widthAuto
            noMask
            tiny
            onClick={() => {
              handleChangeFilter({
                origin: data && data.origin_id,
                startDate:
                  data &&
                  data.creation_date &&
                  moment.unix(data.creation_date).startOf('day').unix(),
                endDate:
                  data &&
                  data.creation_date &&
                  moment.unix(data.creation_date).endOf('day').unix(),
              });
            }}
          >
            {intl.formatMessage({
              id: 'PHOTO_DOCUMENT_DETAILS.FIND_OTHER_FROM_THIS_TIME_AND_ORIGIN',
            })}
          </OptionButton>
        ) : (
          ''
        )}
        {availableRelabel ? (
          <OptionButton
            theme={theme.button.normal}
            widthAuto
            noMask
            tiny
            disabled={
              !data ||
              (data && data.reserved) ||
              (data &&
                !data.rejected &&
                !(data.vinne_annotation && data.vinne_annotation.annotated)) ||
              isLoadingRelabel ||
              isLoadingReject ||
              isLoadingDelete
                ? true
                : undefined
            }
            onClick={() => {
              if (
                !(
                  !data ||
                  (data && data.reserved) ||
                  (data &&
                    !data.rejected &&
                    !(
                      data.vinne_annotation && data.vinne_annotation.annotated
                    )) ||
                  isLoadingRelabel ||
                  isLoadingReject ||
                  isLoadingDelete
                )
              )
                handleActionPhotoDocument({ type: 'RELABEL' });
            }}
          >
            {intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.RELABEL' })}
          </OptionButton>
        ) : (
          ''
        )}
        {availableReject ? (
          <OptionButton
            theme={theme.button.normal}
            widthAuto
            noMask
            tiny
            disabled={
              !data ||
              (data && data.rejected) ||
              isLoadingRelabel ||
              isLoadingReject ||
              isLoadingDelete
                ? true
                : undefined
            }
            onClick={() => {
              if (
                !(
                  !data ||
                  (data && data.rejected) ||
                  isLoadingRelabel ||
                  isLoadingReject ||
                  isLoadingDelete
                )
              )
                handleActionPhotoDocument({ type: 'REJECT' });
            }}
          >
            {intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.REJECT' })}
          </OptionButton>
        ) : (
          ''
        )}
        {availableDelete &&
        isProfileCompany &&
        isLoaded(profileCompany) &&
        profileCompanyRole &&
        authRole &&
        authRole.privileges &&
        authRole.privileges['DELETE_PHOTO'] ? (
          <OptionButton
            theme={theme.button.danger}
            widthAuto
            noMask
            tiny
            disabled={
              isLoadingRelabel || isLoadingReject || isLoadingDelete
                ? true
                : undefined
            }
            onClick={() => {
              if (!(isLoadingRelabel || isLoadingReject || isLoadingDelete))
                handleActionPhotoDocument({ type: 'DELETE' });
            }}
          >
            <OptionButtonIcon className='material-icons'>
              delete_outline
            </OptionButtonIcon>
            {intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.DELETE' })}
          </OptionButton>
        ) : (
          ''
        )}
        {width > Breakpoints.md ? <Grow /> : ''}
        {availableDownload ? (
          <OptionButton
            theme={theme.button.default}
            widthAuto
            noMask
            tiny
            disabled={!(storageVideo || storageImage) ? true : undefined}
            onClick={() => {
              if (storageVideo || storageImage) handleDownload();
            }}
          >
            {intl.formatMessage({ id: 'PHOTO_DOCUMENT_DETAILS.DOWNLOAD' })}
          </OptionButton>
        ) : (
          ''
        )}
      </Footer>
    </Wrapper>
  );
};

const PhotoDocumentDetails = ({
  data,
  handleClose = () => {},
  handleActionPhotoDocument = () => {},
  handleChangePhotoDocument = () => {},
  availableChangeLeftPhotoDocument = false,
  availableChangeRightPhotoDocument = false,
  availableChangeFilter = false,
  availableRelabel = false,
  availableReject = false,
  availableDelete = false,
  availableDownload = false,
}) => {
  return (
    <>
      {data ? (
        <Modal handleClose={handleClose}>
          <PhotoDocumentDetailsInner
            data={data}
            handleClose={handleClose}
            handleActionPhotoDocument={handleActionPhotoDocument}
            handleChangePhotoDocument={handleChangePhotoDocument}
            availableChangeLeftPhotoDocument={availableChangeLeftPhotoDocument}
            availableChangeRightPhotoDocument={
              availableChangeRightPhotoDocument
            }
            availableChangeFilter={availableChangeFilter}
            availableRelabel={availableRelabel}
            availableReject={availableReject}
            availableDelete={availableDelete}
            availableDownload={availableDownload}
          />
        </Modal>
      ) : (
        ''
      )}
    </>
  );
};

PhotoDocumentDetails.propTypes = {
  data: PropTypes.object,
  handleClose: PropTypes.func,
  handleActionPhotoDocument: PropTypes.func,
  handleChangePhotoDocument: PropTypes.func,
  availableChangeLeftPhotoDocument: PropTypes.bool,
  availableChangeRightPhotoDocument: PropTypes.bool,
};

PhotoDocumentDetailsInner.propTypes = {
  data: PropTypes.object,
  handleClose: PropTypes.func,
  handleActionPhotoDocument: PropTypes.func,
  handleChangePhotoDocument: PropTypes.func,
  availableChangeLeftPhotoDocument: PropTypes.bool,
  availableChangeRightPhotoDocument: PropTypes.bool,
};

export default PhotoDocumentDetails;
